/* eslint-disable complexity */
import React, { PropsWithChildren, Children } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { token } from "@atlaskit/tokens";

import {
    getColor,
    ExportableAdgColorTypes,
    DataEventProps
} from "../../../../libraries/wpl/foundations/src/index";

//Types
export type LinkType =
    | "basic"
    | "basic-underline"
    | "external"
    | "external-icon"
    | "video"
    | "modal"
    | "anchor"
    | "inline"
    | "inline-basic";

export type BgColors = "bgWhite" | "bgLight" | "bgDark";

interface LinkColors {
    bgWhite: Colors;
    bgLight: Colors;
    bgDark: Colors;
}

interface Colors {
    default: string;
    hover: string;
    visited: string;
}

export interface LinkProps {
    text: string;
    href: string;
    type?: LinkType;
    customLinkColor?: ExportableAdgColorTypes;
    bgColor?: BgColors;
    onClick?: (e: React.MouseEvent) => void;
    className?: string;
    doesArrowAnimate?: boolean;
    dataEventProps?: DataEventProps;
}

//CSS
const linkColors: LinkColors = {
    bgWhite: {
        default: "#0052cc",
        hover: "#0065ff",
        visited: "#403294"
    },
    bgLight: {
        default: "#172b4d",
        hover: "#0049b0",
        visited: "#403294"
    },
    bgDark: {
        default: "#fafbfc",
        hover: "#4c9aff",
        visited: "#c0b6f2"
    }
};

function linkStyle(
    bgColor?: BgColors,
    type?: LinkType,
    customColor?: ExportableAdgColorTypes,
    doesArrowAnimate?: boolean
) {
    const bg = bgColor ? bgColor : "bgWhite";

    let colorDefault = linkColors[bg].default;
    let colorHover = linkColors[bg].hover;
    let colorVisited = linkColors[bg].visited;

    if (customColor) {
        colorDefault = colorHover = colorVisited = getColor(customColor);
    }
    const linkStyles = css`
        align-items: center;
        color: ${colorDefault};
        display: inline-flex;
        &:visited {
            color: ${colorVisited};
        }
        &:hover,
        &:focus {
            color: ${colorHover};
            ${customColor ? "text-shadow: 0 0 1px, 0 0 1px;" : ""}
        }
        svg,
        path {
            fill: ${colorDefault};
        }
        &:visited svg path {
            fill: ${colorVisited};
        }
        &:hover svg path,
        &:focus svg path {
            fill: ${colorHover};
        }
        ${type === "basic-underline" && `&:hover {text-decoration: underline;}`}
        ${type === "modal"
            ? `text-decoration: underline dashed 1px;
        text-underline-offset: 3px;`
            : type === "inline" || type === "inline-basic"
            ? `text-decoration: underline;
          text-underline-offset: 3px`
            : `text-decoration: none;`}

        svg {
            transition: transform 0.25s ease-in-out;
        }
        ${doesArrowAnimate &&
        (type === "basic" ||
            type === "basic-underline" ||
            type === "inline-basic")
            ? `
          &:hover {
              text-shadow: none;
              svg {
                  transform: translateX(
                      ${token("space.075")}
                  );
              }
          }
        `
            : ""}
    `;
    return linkStyles;
}

//Functions
const Icon = (type: LinkType) => {
    if (
        type === "basic" ||
        type === "basic-underline" ||
        type === "inline-basic"
    ) {
        return (
            <svg
                css={css`
                    margin: 5px 0px 2px 5px;
                    transition: transform 0.25s ease-in-out;
                `}
                width="11"
                height="8"
                viewBox="0 0 11 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.40796 0L6.32831 1.2021L8.07854 3.1497H0.763182C0.341906 3.1497 0 3.53038 0 3.99943C0 4.46849 0.341906 4.84917 0.763182 4.84917H8.07854L6.32831 6.7979L7.40796 8L11 3.99943L7.40796 0Z"
                />
            </svg>
        );
    }
    if (type === "external" || type === "external-icon") {
        return (
            <svg
                css={css`
                    margin: 5px 0px 2px 5px;
                `}
                width="7"
                height="8"
                viewBox="0 0 7 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.04815 0.5V1.98788H4.46059L0.217983 6.22935C-0.0726609 6.51998 -0.0726609 6.99114 0.217983 7.28178C0.363801 7.4266 0.553265 7.5 0.744713 7.5C0.935169 7.5 1.12463 7.4266 1.27045 7.28178L5.51206 3.04031V5.45168H7V0.5H2.04815Z"
                />
            </svg>
        );
    }
    if (type === "video") {
        return (
            <svg
                css={css`
                    margin-right: 3px;
                `}
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14ZM10.3561 7.10299L4.79449 4.21918V9.78082L10.3561 7.10299Z"
                />
            </svg>
        );
    }
    return;
};

const formatLinkText = (text: string, type: LinkType | undefined) => {
    const typeOfLink = type ? type : "basic";
    if (type === "video") {
        return (
            <React.Fragment>
                {Icon(typeOfLink)}
                <span>{text}</span>
            </React.Fragment>
        );
    }
    if (type === "external-icon") {
        return (
            <React.Fragment>
                <span>
                    {text}
                    {Icon(typeOfLink)}
                </span>
            </React.Fragment>
        );
    }
    return (
        <React.Fragment>
            <span>{text}</span>
            {Icon(typeOfLink)}
        </React.Fragment>
    );
};

export const ConditionalWrapper = (props: PropsWithChildren<LinkProps>) => {
    const children = Children.toArray(props.children);
    if (props.type === "inline" || props.type === "inline-basic") {
        return (
            <React.Fragment>{children.map((child) => child)}</React.Fragment>
        );
    }
    return <div className="link-wrapper">{children.map((child) => child)}</div>;
};

export const Link = ({
    text,
    href,
    type,
    customLinkColor,
    bgColor,
    onClick,
    className,
    doesArrowAnimate = false,
    dataEventProps
}: LinkProps) => {
    return (
        <ConditionalWrapper type={type} text={text} href={href}>
            <a
                className={className}
                href={href}
                target={type == "external" ? "_blank" : ""}
                rel={type == "external" ? "noreferrer" : ""}
                css={linkStyle(
                    bgColor,
                    type,
                    customLinkColor,
                    doesArrowAnimate
                )}
                onClick={onClick}
                {...dataEventProps}
            >
                {formatLinkText(text, type)}
            </a>
        </ConditionalWrapper>
    );
};
